/**
 * The collections tray: quotes a teammate has gathered, and their export.
 *
 * The tray is the point of the tool — a teammate builds a quote sheet for a
 * speech, a deck or a film. Both exports carry a full citation per quote so a
 * quote is never separated from its source.
 */
import { formatCitation } from "./citation";
import { formatTimestamp } from "./transcript";
import type { CollectedQuote } from "./types";

function citationOf(quote: CollectedQuote): string {
  return formatCitation({
    quote: quote.en,
    speaker: quote.speaker,
    title: quote.title,
    url: quote.url,
    t: quote.t,
  });
}

/** Plain text: one cited quote per block, ready to paste anywhere. */
export function collectionToText(quotes: readonly CollectedQuote[]): string {
  return quotes.map(citationOf).join("\n\n") + (quotes.length > 0 ? "\n" : "");
}

/** Markdown: the English as a blockquote, the original beneath it, then the source. */
export function collectionToMarkdown(
  quotes: readonly CollectedQuote[],
): string {
  const blocks = quotes.map((quote) => {
    const lines = [`> "${quote.en}"`];
    if (quote.orig.trim().length > 0) {
      lines.push(">", `> ${quote.orig}`);
    }
    const at = formatTimestamp(quote.t);
    const source = `${quote.url}?t=${Math.max(0, Math.floor(quote.t))}`;
    lines.push(
      "",
      `${quote.speaker}, ${quote.title} ([watch at ${at}](${source}))`,
    );
    return lines.join("\n");
  });
  return `# Vihangam Yog quotes\n\n${blocks.join("\n\n")}\n`;
}
